import { useEffect, useState } from 'react'
import type { BonusDoc, DayDoc, DayResult } from '../lib/types'
import { loadBonus, isoWeek } from '../lib/day'
import { getResult, saveResult } from '../lib/storage'
import { syncResult } from '../lib/user'
import { submitResults } from '../lib/histogram'
import { emojiForResult, formatBig, timesOff, shareLine } from '../lib/scoring'
import { share } from '../lib/share'
import { Play } from './Play'
import { TopBar } from './TopBar'
import { BottomNav } from './BottomNav'
import { renderWhen } from '../lib/day'

/** The weekly bonus: 5 general-knowledge questions, live frozen facts, 30s each. */
export function Bonus({ onZoom }: { onZoom: (z: number) => void }) {
  const week = isoWeek()
  const key = `${week}-bonus`
  const [bonus, setBonus] = useState<BonusDoc | null | 'missing'>(null)
  const [started, setStarted] = useState(false)
  const [result, setResult] = useState<DayResult | undefined>(() => getResult(key))
  const [toast, setToast] = useState('')
  useEffect(() => { loadBonus(week).then((b) => setBonus(b ?? 'missing')) }, [week])

  if (bonus === null) return <div className="screen landing"><p className="muted">Loading the bonus…</p></div>
  if (bonus === 'missing') return <div className="screen landing"><p className="muted">No bonus game this week yet — check back soon.</p><a className="btn" href="#/">Back to today</a></div>

  const asDay: DayDoc = { date: key, number: 0, theme: bonus.theme, questions: bonus.questions, generatedAt: bonus.generatedAt }

  if (result) {
    const timeouts = result.results.filter((r) => r.timedOut).length
    const doShare = async () => {
      const r = await share(`Way Off ${bonus.theme.emoji} Weekly Bonus ${week.slice(5)}\n${shareLine(result.emoji, result.score)}${timeouts ? `\n⏰ timed out ×${timeouts}` : ''}\nhttps://wayoffgame.com`)
      setToast(r === 'copied' ? 'Copied to clipboard' : r === 'shared' ? 'Shared!' : 'Could not share')
      setTimeout(() => setToast(''), 1800)
    }
    return (
      <div className="screen results">
        <TopBar />
        <h1>⚡ Bonus done.</h1>
        <p className="sub">Week {week.slice(5)} · new bonus every Monday</p>
        <div className="emoji-grid">{[...result.emoji].map((e, k) => <span key={k}>{e}</span>)}</div>
        <div className="card"><h3>Score</h3><div className="score">{result.score.toLocaleString('en-US')}<small>/5,000</small></div></div>
        <div className="actions">
          <button className="btn block" onClick={doShare}>Share (no spoilers) ↗</button>
          <a className="btn ghost block" href="#/">Back to today</a>
        </div>
        <ul className="qlist">
          {bonus.questions.map((q, k) => { const r = result.results[k]; if (!r) return null; return (
            <li key={q.id}><span className="em">{emojiForResult(r)}</span><span><div>{renderWhen(q)}</div><div className="p">{r.timedOut ? '⏰ timed out' : `you: ${formatBig(r.guess)} · within ${timesOff(r.ratio)}`} · +{r.qscore.toLocaleString('en-US')}</div></span><span className="a">{formatBig(q.answer)}<small>{q.unit}</small></span></li>
          ) })}
        </ul>
        {toast && <div className="toast">{toast}</div>}
        <BottomNav />
      </div>
    )
  }

  if (!started) {
    return (
      <div className="screen landing">
        <TopBar />
        <div style={{ flex: 1 }} />
        <h1 style={{ fontSize: 44 }}>⚡ Weekly<br />Bonus</h1>
        <p>5 random questions from anywhere — including numbers frozen from live data this week.</p>
        <div className="card" style={{ textAlign: 'left' }}>
          <h3>The catch</h3>
          <div className="stat">⏱ {bonus.secondsPerQuestion} seconds per question</div>
          <div className="tiny">run out of time and it's a ⏰ — zero points. No time to Google. One play per week.</div>
        </div>
        <button className="btn block" onClick={() => setStarted(true)}>I'm ready</button>
        <div style={{ flex: 1 }} />
        <BottomNav />
      </div>
    )
  }

  return <Play day={asDay} replay practice timed={bonus.secondsPerQuestion} onZoom={onZoom} onDone={(r) => {
    const dr = { ...r, replay: true } // never touches streaks/wrapped
    saveResult(dr)
    syncResult(dr, key)
    submitResults(key, r.results, r.score).catch(() => {})
    setResult(dr)
  }} />
}
